import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import useDirectory from "../hooks/useDirectory";
import useTaskActions from "../hooks/useTaskActions";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import {
  STAGES, MANUAL_STAGES, STAGE_LABEL, INTERACTION_TYPES, INTERACTION_LABELS, SYSTEM_MANAGED_STAGES, formatRelativeDate, ACQUISITION_CHANNEL_LABEL, parseAcquisitionChannels,
} from "../lib/pipeline";
import { onboardingEmptyState, onboardingSummaryLabel } from "../lib/onboarding";
import StatusExtraFields, { defaultStatusExtra, isStatusExtraValid, buildStatusExtraPayload } from "../components/StatusExtraFields";
import { EVENT_LABELS, describeActivityEvent, isViaCopilot } from "../lib/activity";
import ViaCopilotBadge from "../components/ViaCopilotBadge";
import TaskFormSheet from "../components/TaskFormSheet";
import CompleteTaskDialog from "../components/CompleteTaskDialog";
import { TaskDetailSheet, DeleteTaskDialog } from "../components/TaskDetailSheet";
import AssigneeAvatars from "../components/AssigneeAvatars";
import { formatDueDay } from "../lib/tasks";
import AcquisitionChannelField from "../components/AcquisitionChannelField";
import PilotPanel from "../components/PilotPanel";
import PilotHealthBadge from "../components/PilotHealthBadge";
import { ProspectLoadError, ProspectUnavailable } from "../components/ProspectLoadStates";
import { canOfferPilotLink, classifyProspectLoad } from "../lib/prospectLoad";
import { isRunning, shouldShowPilotTab, taskPerformerOptions } from "../lib/pilots";
import usePilot from "../hooks/usePilot";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuTrigger as DropdownMenuTrigger, ResponsiveDropdownMenuContent as DropdownMenuContent,
  ResponsiveDropdownMenuItem as DropdownMenuItem, ResponsiveDropdownMenuSeparator as DropdownMenuSeparator,
} from "../components/ui/responsive-dropdown-menu";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, MapPin, Phone, Mail, MessageSquareText, CheckCircle2, Circle, Clock, Rocket, FileText, ChevronDown, ChevronRight, Cog, Pencil, X, MoreHorizontal, UserPlus, PhoneCall, ClipboardList, ArrowUpRight, Plus, Trash2,
} from "lucide-react";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import RelatedMissions from "../components/missions/RelatedMissions";
import { canSeeMissions } from "../lib/missions";
import { canSeeEveryPark } from "../lib/equipment";
import PharmacyEquipmentTab from "../components/equipment/PharmacyEquipmentTab";
import PharmacyTickets from "../components/tickets/PharmacyTickets";
import { canSeeTickets } from "../lib/tickets";
import RelatedSessions from "../components/training/RelatedSessions";
import { CATALOG_READ_KEYS } from "../lib/training";
import OnboardingTab from "../components/prospects/OnboardingTab";
import MigrationTab from "../components/prospects/MigrationTab";
import CustomerPanel from "../components/customers/CustomerPanel";
import ProspectOrigin from "../components/marketing/ProspectOrigin";
import { CONVERTED_STAGES, canSeeCustomers } from "../lib/customers";
import {
  INFO_FIELD_GROUPS, INFO_EDITABLE_KEYS, INTERACTION_ICONS, MIGRATION_STATUS_LABEL, submissionFieldLabel, groupByDay,
} from "../lib/prospectDetail";

export default function ProspectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canChangeStatus = hasPermission(user, "prospects:change_status");
  const canAddInteraction = hasPermission(user, "prospects:add_interaction");
  const canDeleteInteraction = hasPermission(user, "prospects:delete_interaction");
  const [deletingInteraction, setDeletingInteraction] = useState(null);
  const canManageOnboarding = hasPermission(user, "prospects:manage_onboarding");
  const canManageMigration = hasPermission(user, "prospects:manage_migration");
  const canAssign = hasPermission(user, "prospects:assign");
  const canCreateTasks = hasPermission(user, "tasks:create");
  const canEditTasks = hasPermission(user, "tasks:edit");
  const canDeleteTasks = hasPermission(user, "tasks:delete");
  const canScopeAllTasks = hasPermission(user, "tasks:scope_all");
  const canEditInfo = hasPermission(user, "prospects:edit");
  const hasQuickActions = canEditInfo || canAssign || canAddInteraction || canCreateTasks;

  // A link may open one tab directly (« /prospects/:id?tab=missions », from the customer account's field summary).
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => searchParams.get("tab") || "apercu");
  // On a phone the tab bar scrolls sideways: a tab opened from a link is brought into view instead of staying off-screen.
  const linkedTab = searchParams.get("tab");
  const [p, setP] = useState(null);
  useEffect(() => {
    if (!linkedTab || !p?.id) return;
    document.querySelector(`[data-testid="tab-${linkedTab}"]`)?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [linkedTab, p?.id]);
  const [tasks, setTasks] = useState([]);
  const [activity, setActivity] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [expandedSubmissionId, setExpandedSubmissionId] = useState(null);
  const [editingInfo, setEditingInfo] = useState(false);
  const [infoForm, setInfoForm] = useState({});
  const [savingInfo, setSavingInfo] = useState(false);
  const [statusDialog, setStatusDialog] = useState({ open: false, status: null });
  const [statusExtra, setStatusExtra] = useState({});
  const [onboarding, setOnboarding] = useState(null);
  const [migrationObj, setMigrationObj] = useState(null);
  // The pilot of this pharmacy (0..1): {status: "loading" | "none" | "ready" | "error", data?, error?}.
  const { pilotState, loadPilot, setPilotData } = usePilot(id);
  const canManagePilot = hasPermission(user, "prospects:manage_pilot");

  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignUserId, setAssignUserId] = useState("__none__");
  const [savingAssign, setSavingAssign] = useState(false);

  const [interactionDialogOpen, setInteractionDialogOpen] = useState(false);
  const [interactionKind, setInteractionKind] = useState("call");
  const [interactionContent, setInteractionContent] = useState("");
  const [savingInteraction, setSavingInteraction] = useState(false);

  const [taskFormOpen, setTaskFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [viewingTask, setViewingTask] = useState(null);

  // `initial` (first load, new id, retry) starts from a clean slate and shows loading / unavailable / error screens; a plain
  // call is a refresh after an action and never blanks the page. A response that arrives after a newer load (the id changed
  // meanwhile) is dropped, so a fiche never shows another prospect's data.
  const [loadState, setLoadState] = useState({ status: "loading" });
  const loadSeq = useRef(0);
  const load = async ({ initial = false } = {}) => {
    const mine = ++loadSeq.current;
    const stale = () => mine !== loadSeq.current;
    if (initial) {
      setLoadState({ status: "loading" });
      setP(null); setTasks([]); setActivity([]); setSubmissions([]); setOnboarding(null); setMigrationObj(null);
    }
    let prospect;
    try {
      ({ data: prospect } = await api.get(`/prospects/${id}`));
    } catch (e) {
      if (stale()) return;
      const kind = classifyProspectLoad(e);
      if (initial || kind === "unavailable") { setP(null); setLoadState(kind === "unavailable" ? { status: "unavailable" } : { status: "error" }); }
      else toast.error("Impossible d'actualiser la fiche.");
      return;
    }
    if (stale()) return;
    setP(prospect);
    setLoadState({ status: "ready" });
    // Secondary data: each one on its own, so one refusal (e.g. no tasks:view) neither stops the others nor leaves a rejection.
    let partial = false;
    const part = async (path, config, apply) => {
      try {
        const { data } = await api.get(path, config);
        if (!stale()) apply(data);
      } catch (e) {
        if (!stale() && classifyProspectLoad(e) === "error") partial = true;  // 403/404 = simply not available to this caller
      }
    };
    await part("/tasks", { params: { prospect_id: id } }, setTasks);
    await part(`/prospects/${id}/activity`, undefined, setActivity);
    await part(`/prospects/${id}/submissions`, undefined, setSubmissions);
    if (partial && !stale()) toast.error("Certaines informations de la fiche n'ont pas pu être chargées.");
    // Onboarding/Migration may not have started yet: 404 is expected, not an error.
    try {
      const { data: o } = await api.get(`/prospects/${id}/onboarding`);
      if (!stale()) setOnboarding(o);
    } catch (e) {
      if (!stale()) setOnboarding(null);
    }
    try {
      const { data: m } = await api.get(`/prospects/${id}/migration`);
      if (!stale()) setMigrationObj(m);
    } catch (e) {
      if (!stale()) setMigrationObj(null);
    }
  };
  useEffect(() => {
    load({ initial: true });
    return () => { loadSeq.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (activeTab === "pilot") loadPilot({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  const directory = useDirectory(canAssign || canScopeAllTasks || canEditTasks);

  const {
    completing, setCompleting, deletingTask, setDeletingTask,
    markDone: markTaskDone, cycleStatus: cycleTaskStatus, toggleDone: toggleTask, confirmComplete,
    postpone: postponeTask, addAssignee: addTaskAssignee, confirmDelete: confirmDeleteTask,
  } = useTaskActions({
    // Local update rather than a full load(): keeps an open task drawer in sync without re-fetching the whole page.
    onUpdated: (updated) => {
      setTasks((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      setViewingTask((prev) => (prev && prev.id === updated.id ? updated : prev));
    },
    onDeleted: (task) => {
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
      setViewingTask((prev) => (prev && prev.id === task.id ? null : prev));
    },
    onFormCompleted: () => { setTaskFormOpen(false); setEditingTask(null); },
  });

  if (loadState.status === "unavailable") return <ProspectUnavailable prospectId={id} pilotConfirmed={canOfferPilotLink(pilotState, id)} />;
  if (loadState.status === "error") return <ProspectLoadError onRetry={() => load({ initial: true })} />;
  if (!p) return (
    <div className="flex justify-center py-20"><Loader2 className="animate-spin text-jamora" /></div>
  );

  const userName = (userId) => {
    if (!userId) return null;
    if (userId === user?.id) return user.name;
    return directory.find((u) => u.id === userId)?.name || null;
  };
  const directoryNames = (ids) => {
    const names = (ids || []).map(userName).filter(Boolean);
    return names.length ? names.join(", ") : null;
  };
  const resolveAssignee = (id) => {
    if (id === user?.id) return { name: user.name, avatar_url: user.avatar_url };
    const found = directory.find((u) => u.id === id);
    return found ? { name: found.name, avatar_url: found.avatar_url } : null;
  };

  const changeStatus = (status) => {
    if (status === p.status) return;
    setStatusExtra(defaultStatusExtra(status));
    setStatusDialog({ open: true, status });
  };

  const confirmStatusChange = async () => {
    if (!isStatusExtraValid(statusDialog.status, statusExtra)) {
      toast.error("Merci de compléter les informations requises.");
      return;
    }
    try {
      await api.post(`/prospects/${p.id}/status`, {
        status: statusDialog.status,
        ...buildStatusExtraPayload(statusDialog.status, statusExtra),
      });
      toast.success("Statut mis à jour");
      setStatusDialog({ open: false, status: null });
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Erreur lors du changement de statut"));
    }
  };

  const openInteractionDialog = () => {
    setInteractionKind("call");
    setInteractionContent("");
    setInteractionDialogOpen(true);
  };
  const saveInteraction = async () => {
    if (!interactionContent.trim()) {
      toast.error("Le compte-rendu ne peut pas être vide");
      return;
    }
    setSavingInteraction(true);
    try {
      await api.post(`/prospects/${p.id}/interactions`, { kind: interactionKind, content: interactionContent });
      toast.success("Interaction ajoutée");
      setInteractionDialogOpen(false);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Erreur lors de l'ajout"));
    } finally {
      setSavingInteraction(false);
    }
  };

  const toggleModule = async (phase_id, module_id, current_status) => {
    const nextStatus = current_status === "done" ? "todo" : current_status === "in_progress" ? "done" : "in_progress";
    try {
      await api.patch(`/prospects/${p.id}/roadmap`, { phase_id, module_id, status: nextStatus });
    } catch (e) {
      toast.error(errorMessage(e, "Impossible de mettre à jour le module."));
    }
    load();
  };

  const updateActualHours = async (phase_id, module_id, hours) => {
    try {
      await api.patch(`/prospects/${p.id}/roadmap`, { phase_id, module_id, actual_hours: Number(hours) });
    } catch (e) {
      toast.error(errorMessage(e, "Impossible d'enregistrer les heures."));
    }
    load();
  };

  const startOnboarding = async () => {
    try {
      await api.post(`/prospects/${p.id}/start-onboarding`);
      toast.success("Onboarding démarré");
    } catch (e) {
      toast.error(errorMessage(e, "Impossible de lancer l'onboarding."));
    }
    load();
  };

  const updateMigration = async (patch) => {
    try {
      await api.post(`/prospects/${p.id}/migration`, patch);
    } catch (e) {
      toast.error(errorMessage(e, "Impossible d'enregistrer la migration."));
    }
    load();
  };

  const onTaskSaved = (task, isNew) => {
    setTasks((prev) => (isNew ? [task, ...prev] : prev.map((t) => (t.id === task.id ? task : t))));
    setViewingTask((prev) => (prev && prev.id === task.id ? task : prev));
    setTaskFormOpen(false);
  };

  const openEditTask = (t) => { setViewingTask(null); setEditingTask(t); setTaskFormOpen(true); };


  const openAssignDialog = () => {
    setAssignUserId(p.assigned_to || "__none__");
    setAssignDialogOpen(true);
  };
  const saveAssign = async () => {
    setSavingAssign(true);
    try {
      await api.post(`/prospects/${p.id}/assign`, { user_id: assignUserId === "__none__" ? null : assignUserId });
      toast.success("Prospect assigné");
      setAssignDialogOpen(false);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Erreur lors de l'assignation"));
    } finally {
      setSavingAssign(false);
    }
  };

  const startEditInfo = () => {
    const initial = {};
    for (const key of INFO_EDITABLE_KEYS) initial[key] = p[key] || "";
    setInfoForm(initial);
    setEditingInfo(true);
  };
  const openModifier = () => {
    startEditInfo();
    setActiveTab("info");
  };

  const cancelEditInfo = () => setEditingInfo(false);

  const saveInfo = async () => {
    if (!infoForm.pharmacy_name.trim()) {
      toast.error("Le nom de la pharmacie est requis");
      return;
    }
    setSavingInfo(true);
    try {
      await api.patch(`/prospects/${p.id}`, infoForm);
      toast.success("Informations enregistrées");
      setEditingInfo(false);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Erreur lors de l'enregistrement"));
    } finally {
      setSavingInfo(false);
    }
  };

  const roadmap = onboarding?.phases || [];
  const total_m = roadmap.reduce((a, ph) => a + ph.modules.length, 0);
  const done_m = roadmap.reduce((a, ph) => a + ph.modules.filter((m) => m.status === "done").length, 0);
  const pct = total_m ? Math.round((done_m / total_m) * 100) : 0;
  const showPilotTab = shouldShowPilotTab({ pilotState, canManagePilot, prospectStatus: p.status });
  const showMissionsTab = canSeeMissions(user);
  const showEquipmentTab = canSeeEveryPark(user);
  const showTrainingTab = !!user?.features?.training && hasPermission(user, CATALOG_READ_KEYS);
  // Support tickets (Ordre 08): the tab lists the pharmacy's tickets within the reader's own ticket scope.
  const showSupportTab = canSeeTickets(user);
  // The customer account (Ordre 07): offered on a converted pharmacy to whoever may see customer accounts; the panel itself says
  // when the account is out of the reader's own portfolio.
  const showCustomerTab = canSeeCustomers(user) && CONVERTED_STAGES.includes(p.status);
  const tabCount = 7 + (showCustomerTab ? 1 : 0) + (showPilotTab ? 1 : 0) + (showMissionsTab ? 1 : 0) + (showEquipmentTab ? 1 : 0) + (showTrainingTab ? 1 : 0) + (showSupportTab ? 1 : 0);
  const onboardingEmpty = onboardingEmptyState(p.status, roadmap.length);
  const onboardingInconsistent = onboardingEmpty === "repair";

  const notDoneTasks = tasks.filter((t) => !t.done);
  const doneTasks = tasks.filter((t) => t.done);
  const nextTask = notDoneTasks
    .filter((t) => t.due_date)
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))[0];

  const journeyStages = STAGES.filter((s) => s.id !== "lost");
  const journeyIndex = journeyStages.findIndex((s) => s.id === p.status);

  const renderActivityEntry = (e) => {
    const isInteraction = e.kind === "interaction";
    const Icon = isInteraction ? (INTERACTION_ICONS[e.type] || FileText) : Cog;
    const label = isInteraction ? (INTERACTION_LABELS[e.type] || e.type) : (EVENT_LABELS[e.event_type] || e.event_type);
    const authorName = isInteraction ? userName(e.author_id) : userName(e.actor_id);
    return (
      <li key={e.id} className="flex gap-3">
        <div className="mt-0.5 flex-shrink-0 w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center">
          <Icon size={14} className={isInteraction ? "text-jamora" : "text-slate-400"} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</div>
          <div className="text-sm text-slate-900">
            {isInteraction ? e.content : describeActivityEvent(e)}
          </div>
          <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-1.5">
            <span>
              {authorName && <>{authorName} · </>}
              {new Date(e.occurred_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
            </span>
            {isViaCopilot(e) && <ViaCopilotBadge />}
            {isInteraction && canDeleteInteraction && (
              <button type="button" onClick={() => setDeletingInteraction(e)} aria-label="Supprimer cet échange" data-testid={`interaction-delete-${e.id}`}
                className="inline-flex h-6 w-6 items-center justify-center rounded text-slate-400 hover:bg-red-50 hover:text-red-700 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11">
                <Trash2 size={13} />
              </button>
            )}
          </div>
        </div>
      </li>
    );
  };

  return (
    <div className="space-y-4" data-testid="prospect-detail-page">
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
      >
        <ArrowLeft size={14} /> Retour
      </button>

      <Card className="relative p-5 md:p-6">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div>
            <h1 className="font-display font-bold text-2xl text-slate-900 pr-12 md:pr-0 break-words">{p.pharmacy_name}</h1>
            <div className="flex flex-wrap gap-3 mt-2 text-sm text-slate-600">
              {(p.commune || p.region) && (
                <span className="inline-flex items-center gap-1"><MapPin size={14} />{[p.commune, p.region].filter(Boolean).join(", ")}</span>
              )}
              {p.phone && <span className="inline-flex items-center gap-1"><Phone size={14} />{p.phone}</span>}
              {p.email && <span className="inline-flex items-center gap-1"><Mail size={14} />{p.email}</span>}
              {p.whatsapp && <span className="inline-flex items-center gap-1"><MessageSquareText size={14} />{p.whatsapp}</span>}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <span className={`text-2xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded stage-${p.status}`}>
                {STAGE_LABEL[p.status]}
              </span>
              {p.loss_reason && <Badge variant="destructive">{p.loss_reason}</Badge>}
              {pilotState.status === "ready" && isRunning(pilotState.data) && pilotState.data.computed?.health && (
                <button type="button" onClick={() => setActiveTab("pilot")} aria-label="Voir le pilote" data-testid="header-pilot-badge">
                  <PilotHealthBadge state={pilotState.data.computed.health.state} testid="header-pilot-health" />
                </button>
              )}
              {p.source && <Badge variant="outline">Source · {p.source}</Badge>}
            </div>
            {nextTask && (
              <div className="text-xs text-slate-500 mt-2 inline-flex items-center gap-1">
                <Clock size={12} /> Prochaine action : {nextTask.title}, {formatDueDay(nextTask.due_date)}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-2 flex-shrink-0">
            {canManageOnboarding && p.status === "won" && (
              <Button
                onClick={startOnboarding}
                data-testid="btn-start-onboarding"
                className="w-full md:w-auto"
              >
                <Rocket size={14} className="mr-1.5" /> Lancer l'onboarding
              </Button>
            )}
            <div className="flex items-center gap-2">
              {canChangeStatus && !SYSTEM_MANAGED_STAGES.includes(p.status) && (
                <Select value={p.status} onValueChange={changeStatus}>
                  <SelectTrigger data-testid="select-detail-status" className="w-44">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MANUAL_STAGES.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {hasQuickActions && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" className="absolute right-5 top-5 md:static shrink-0" aria-label="Actions du prospect" data-testid="btn-prospect-quick-actions">
                      <MoreHorizontal size={16} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {canEditInfo && (
                      <DropdownMenuItem onClick={openModifier} data-testid="action-modifier">
                        <Pencil size={14} className="mr-2" /> Modifier
                      </DropdownMenuItem>
                    )}
                    {canAssign && (
                      <DropdownMenuItem onClick={openAssignDialog} data-testid="action-assigner">
                        <UserPlus size={14} className="mr-2" /> Assigner à…
                      </DropdownMenuItem>
                    )}
                    {(canAddInteraction || canCreateTasks) && (canEditInfo || canAssign) && <DropdownMenuSeparator />}
                    {canAddInteraction && (
                      <DropdownMenuItem onClick={openInteractionDialog} data-testid="action-interaction">
                        <PhoneCall size={14} className="mr-2" /> Ajouter une interaction
                      </DropdownMenuItem>
                    )}
                    {canCreateTasks && (
                      <DropdownMenuItem onClick={() => setTaskFormOpen(true)} data-testid="action-tache">
                        <ClipboardList size={14} className="mr-2" /> Créer une tâche
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`w-full h-auto flex overflow-x-auto justify-start gap-1 md:grid ${{ 7: "md:grid-cols-7", 8: "md:grid-cols-8", 9: "md:grid-cols-9", 10: "md:grid-cols-10", 11: "md:grid-cols-11", 12: "md:grid-cols-12", 13: "md:grid-cols-[repeat(13,minmax(0,1fr))]" }[tabCount]}`}>
          <TabsTrigger value="apercu" data-testid="tab-apercu" className="shrink-0 md:shrink">Aperçu</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline" className="shrink-0 md:shrink">Activité</TabsTrigger>
          <TabsTrigger value="info" data-testid="tab-info" className="shrink-0 md:shrink">Informations</TabsTrigger>
          <TabsTrigger value="submissions" data-testid="tab-submissions" className="shrink-0 md:shrink">Questionnaires</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks" className="shrink-0 md:shrink">Tâches</TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="tab-onboarding" className="shrink-0 md:shrink">Onboarding</TabsTrigger>
          <TabsTrigger value="migration" data-testid="tab-migration" className="shrink-0 md:shrink">Mise en place</TabsTrigger>
          {showCustomerTab && <TabsTrigger value="customer" data-testid="tab-customer" className="shrink-0 md:shrink">Client</TabsTrigger>}
          {showPilotTab && <TabsTrigger value="pilot" data-testid="tab-pilot" className="shrink-0 md:shrink">Pilote</TabsTrigger>}
          {showMissionsTab && <TabsTrigger value="missions" data-testid="tab-missions" className="shrink-0 md:shrink">Missions</TabsTrigger>}
          {showEquipmentTab && <TabsTrigger value="equipment" data-testid="tab-equipment" className="shrink-0 md:shrink">Matériel</TabsTrigger>}
          {showTrainingTab && <TabsTrigger value="training" data-testid="tab-training" className="shrink-0 md:shrink">Formations</TabsTrigger>}
          {showSupportTab && <TabsTrigger value="support" data-testid="tab-support" className="shrink-0 md:shrink">Support</TabsTrigger>}
        </TabsList>

        <TabsContent value="apercu" className="mt-4 space-y-4">
          <Card className="p-5">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Prochaine action</h4>
            {nextTask ? (
              <div className="text-sm text-slate-900">
                {nextTask.title} <span className="text-slate-500">· {formatDueDay(nextTask.due_date)}</span>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-slate-500">Aucune tâche planifiée</span>
                {canCreateTasks && (
                  <Button size="sm" variant="outline" onClick={() => setTaskFormOpen(true)} data-testid="btn-apercu-new-task">
                    <Plus size={14} className="mr-1.5" /> Créer une tâche
                  </Button>
                )}
              </div>
            )}
          </Card>

          <Card className="p-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Responsable</h4>
                <div className="text-sm text-slate-900">{userName(p.assigned_to) || (p.assigned_to ? "Utilisateur" : "Non assigné")}</div>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Dernière activité</h4>
                <div className="text-sm text-slate-900">{formatRelativeDate(p.last_activity_at)}</div>
              </div>
            </div>
          </Card>

          <Card className="p-5">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Progression commerciale</h4>
            {p.status === "lost" ? (
              <div className="text-sm">
                <Badge variant="destructive">Perdu</Badge>
                {p.loss_reason && <span className="text-slate-500 ml-2">Motif : {p.loss_reason}</span>}
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-1.5 text-xs">
                {journeyStages.map((s, i) => (
                  <span key={s.id} className="inline-flex items-center gap-1.5">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full font-medium ${
                        i < journeyIndex ? "bg-emerald-50 text-emerald-700"
                          : i === journeyIndex ? "bg-jamora text-white"
                          : "bg-slate-100 text-slate-400"
                      }`}
                    >
                      {i < journeyIndex ? <CheckCircle2 size={12} /> : i === journeyIndex ? <CircleDotIcon /> : <Circle size={12} />}
                      {s.short}
                    </span>
                    {i < journeyStages.length - 1 && <ChevronRight size={12} className="text-slate-300" />}
                  </span>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Dernières activités</h4>
              <button className="text-xs text-jamora hover:underline" onClick={() => setActiveTab("timeline")}>
                Voir tout
              </button>
            </div>
            {activity.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-2">Aucune activité pour le moment.</div>
            ) : (
              <ol className="space-y-3">{activity.slice(0, 3).map(renderActivityEntry)}</ol>
            )}
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-5 flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-slate-900">Onboarding</h4>
                <div className="text-xs text-slate-500 mt-0.5">
                  {onboardingSummaryLabel(p.status, roadmap.length, done_m, total_m)}
                </div>
              </div>
              <button
                className="text-jamora inline-flex items-center gap-1 text-sm hover:underline"
                onClick={() => setActiveTab("onboarding")}
                data-testid="link-apercu-onboarding"
              >
                Voir <ArrowUpRight size={14} />
              </button>
            </Card>
            <Card className="p-5 flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-slate-900">Mise en place</h4>
                <div className="text-xs text-slate-500 mt-0.5">
                  {migrationObj ? MIGRATION_STATUS_LABEL[migrationObj.status] : "Non démarrée"}
                </div>
              </div>
              <button
                className="text-jamora inline-flex items-center gap-1 text-sm hover:underline"
                onClick={() => setActiveTab("migration")}
                data-testid="link-apercu-migration"
              >
                Voir <ArrowUpRight size={14} />
              </button>
            </Card>
          </div>
          {user?.features?.marketing && <ProspectOrigin prospectId={id} />}
        </TabsContent>

        <TabsContent value="timeline" className="mt-4 space-y-4">
          {canAddInteraction && (
            <div className="flex justify-end">
              <Button onClick={openInteractionDialog} data-testid="btn-add-interaction">
                <Plus size={14} className="mr-1.5" /> Ajouter une interaction
              </Button>
            </div>
          )}
          <Card className="p-4">
            {activity.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-4">Aucune activité pour le moment.</div>
            ) : (
              <div className="space-y-5">
                {groupByDay(activity).map((group) => (
                  <div key={group.label}>
                    <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{group.label}</div>
                    <ol className="space-y-3">{group.entries.map(renderActivityEntry)}</ol>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="submissions" className="mt-4 space-y-3">
          {submissions.length === 0 ? (
            <Card className="p-8 text-center text-sm text-slate-500">
              Aucun questionnaire reçu pour ce prospect.
            </Card>
          ) : (
            submissions.map((s) => {
              const expanded = expandedSubmissionId === s.id;
              const answerEntries = Object.entries(s.answers || {}).filter(([, v]) => v);
              return (
                <Card key={s.id} className="overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-slate-50"
                    onClick={() => setExpandedSubmissionId(expanded ? null : s.id)}
                    data-testid={`submission-row-${s.id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText size={16} className="text-jamora flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900">Questionnaire découverte</div>
                        <div className="text-xs text-slate-500">
                          {new Date(s.submitted_at).toLocaleString("fr-FR")}
                          {s.respondent_email && <> · {s.respondent_email}</>}
                          {" · "}{s.source}
                        </div>
                      </div>
                    </div>
                    {expanded ? <ChevronDown size={16} className="text-slate-400 flex-shrink-0" /> : <ChevronRight size={16} className="text-slate-400 flex-shrink-0" />}
                  </button>
                  {expanded && (
                    <div className="border-t border-slate-100 p-4">
                      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                        {answerEntries.map(([key, value]) => (
                          <div key={key}>
                            <dt className="text-xs text-slate-500 uppercase tracking-wider">{submissionFieldLabel(key)}</dt>
                            <dd className="text-slate-900 mt-0.5">{String(value)}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}
                </Card>
              );
            })
          )}
        </TabsContent>

        <TabsContent value="info" className="mt-4 space-y-4">
          {canEditInfo && (
            <div className="flex justify-end">
              {editingInfo ? (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={cancelEditInfo} disabled={savingInfo} data-testid="btn-cancel-info">
                    <X size={14} className="mr-1.5" /> Annuler
                  </Button>
                  <Button size="sm" onClick={saveInfo} disabled={savingInfo} data-testid="btn-save-info">
                    {savingInfo ? <Loader2 className="animate-spin mr-1.5" size={14} /> : null}
                    Enregistrer
                  </Button>
                </div>
              ) : (
                <Button variant="outline" size="sm" onClick={startEditInfo} data-testid="btn-edit-info">
                  <Pencil size={14} className="mr-1.5" /> Modifier
                </Button>
              )}
            </div>
          )}

          {editingInfo ? (
            <div className="space-y-4">
              {INFO_FIELD_GROUPS.map((group) => (
                <Card key={group.title} className="p-5">
                  <h4 className="font-display font-semibold text-sm text-slate-900 mb-3">{group.title}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {group.fields.map(([key, label, required, type]) => (
                      <div key={key} className="space-y-1.5">
                        <Label htmlFor={`info-${key}`} className="text-xs">
                          {label}{required && <span className="text-red-500 ml-1">*</span>}
                        </Label>
                        {type === "channels" ? (
                          <AcquisitionChannelField
                            value={infoForm[key] || ""}
                            onChange={(v) => setInfoForm((f) => ({ ...f, [key]: v }))}
                          />
                        ) : (
                          <Input
                            id={`info-${key}`}
                            data-testid={`input-info-${key}`}
                            value={infoForm[key] || ""}
                            onChange={(e) => setInfoForm((f) => ({ ...f, [key]: e.target.value }))}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-5 space-y-5">
              {INFO_FIELD_GROUPS.map((group) => (
                <div key={group.title}>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{group.title}</h4>
                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                    {group.fields.map(([key, label, , type]) => (
                      <div key={key}>
                        <dt className="text-xs text-slate-500">{label}</dt>
                        <dd className="text-slate-900 mt-0.5">
                          {type === "channels" ? (
                            parseAcquisitionChannels(p[key]).length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {parseAcquisitionChannels(p[key]).map((id) => (
                                  <Badge key={id} variant="outline" className="text-2xs font-medium">
                                    {ACQUISITION_CHANNEL_LABEL[id] || id}
                                  </Badge>
                                ))}
                              </div>
                            ) : "Non renseigné"
                          ) : (p[key] || "Non renseigné")}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </Card>
          )}
        </TabsContent>

        <TabsContent value="tasks" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-display font-semibold text-slate-900">Tâches ({tasks.length})</h4>
            {canCreateTasks && (
              <Button
                size="sm" onClick={() => { setEditingTask(null); setTaskFormOpen(true); }}
                data-testid="btn-add-task"
              >
                <Plus size={14} className="mr-1.5" /> Nouvelle tâche
              </Button>
            )}
          </div>
          <Card className="p-4">
            {tasks.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-4">Aucune tâche.</div>
            ) : (
              <div className="space-y-4">
                <div>
                  <h5 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">À faire</h5>
                  {notDoneTasks.length === 0 ? (
                    <div className="text-sm text-slate-400 py-2">Aucune tâche en attente.</div>
                  ) : (
                    <ul className="space-y-2">
                      {notDoneTasks.map((t) => (
                        <li key={t.id} data-testid={`task-item-${t.id}`} className="flex items-center gap-3 p-2 rounded hover:bg-slate-50">
                          {canEditTasks ? (
                            <Checkbox checked={t.done} onCheckedChange={() => toggleTask(t)} data-testid={`checkbox-task-${t.id}`} />
                          ) : (
                            <Circle className="text-slate-300" size={18} />
                          )}
                          <div className="flex-1 min-w-0">
                            <button
                              type="button" onClick={() => setViewingTask(t)} data-testid={`view-task-${t.id}`}
                              className="text-sm text-slate-900 text-left hover:underline"
                            >
                              {t.title}
                            </button>
                            {t.due_date && (
                              <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                                <Clock size={11} /> {formatDueDay(t.due_date)}
                              </div>
                            )}
                          </div>
                          <div className="shrink-0">
                            <AssigneeAvatars userIds={t.assigned_to} resolveUser={resolveAssignee} />
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {doneTasks.length > 0 && (
                  <div className="border-t border-slate-100 pt-3">
                    <h5 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Terminées ({doneTasks.length})</h5>
                    <ul className="space-y-2">
                      {doneTasks.map((t) => (
                        <li key={t.id} data-testid={`task-item-${t.id}`} className="flex items-center gap-3 p-2 rounded hover:bg-slate-50">
                          {canEditTasks ? (
                            <Checkbox checked={t.done} onCheckedChange={() => toggleTask(t)} data-testid={`checkbox-task-${t.id}`} />
                          ) : (
                            <CheckCircle2 className="text-jamora-light" size={18} />
                          )}
                          <div className="flex-1">
                            <button
                              type="button" onClick={() => setViewingTask(t)} data-testid={`view-task-${t.id}`}
                              className="text-sm line-through text-slate-400 text-left hover:underline"
                            >
                              {t.title}
                            </button>
                            {t.completed_at && (
                              <div className="text-xs text-slate-400 mt-0.5">Terminée par {t.completed_by_name || "un compte non enregistré"}, le {new Date(t.completed_at).toLocaleString("fr-FR")}</div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="onboarding" className="mt-4 space-y-4">
          <OnboardingTab
            roadmap={roadmap} onboarding={onboarding} emptyState={onboardingEmpty} doneCount={done_m} totalCount={total_m} pct={pct}
            canManage={canManageOnboarding} onRepair={startOnboarding} onToggleModule={toggleModule} onActualHours={updateActualHours}
          />
        </TabsContent>

        <TabsContent value="migration" className="mt-4 space-y-4">
          <MigrationTab migration={migrationObj} canManage={canManageMigration} userName={userName} onUpdate={updateMigration} />
        </TabsContent>
        {showCustomerTab && (
          <TabsContent value="customer" className="mt-4">
            <CustomerPanel prospectId={p.id} />
          </TabsContent>
        )}
        {showEquipmentTab && (
          <TabsContent value="equipment" className="mt-4">
            <PharmacyEquipmentTab prospectId={p.id} userId={user?.id} />
          </TabsContent>
        )}
        {showTrainingTab && (
          <TabsContent value="training" className="mt-4">
            <RelatedSessions params={{ prospect_id: p.id }} title="Formations de la pharmacie" testid="pharmacy-sessions"
              emptyText="Aucune session de formation pour cette pharmacie." />
          </TabsContent>
        )}
        {showSupportTab && (
          <TabsContent value="support" className="mt-4">
            <PharmacyTickets prospectId={p.id} pharmacyName={p.pharmacy_name} />
          </TabsContent>
        )}
        {showMissionsTab && (
          <TabsContent value="missions" className="mt-4">
            <RelatedMissions prospectId={p.id} pilotId={pilotState.status === "ready" ? pilotState.data?.id : null} />
          </TabsContent>
        )}
        {showPilotTab && (
          <TabsContent value="pilot" className="mt-4">
            {pilotState.status === "ready" && (
              <div className="mb-3 text-right">
                <Link to={`/pilotes/${p.id}`} className="text-xs text-jamora hover:underline inline-flex items-center gap-1" data-testid="open-pilot-workspace">
                  Ouvrir l'espace pilote : tâches de l'équipe <ArrowUpRight size={12} />
                </Link>
              </div>
            )}
            <PilotPanel
              prospect={p} pilotState={pilotState} user={user} directory={directory} tasks={tasks}
              onChange={setPilotData} onReload={() => loadPilot()}
              onTasksChanged={() => { load(); loadPilot({ silent: true }); }}
              onOpenTask={(taskId) => { const t = tasks.find((x) => x.id === taskId); if (t) setViewingTask(t); }}
              onGoToTab={setActiveTab}
            />
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={statusDialog.open} onOpenChange={(v) => !v && setStatusDialog({ open: false, status: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {statusDialog.status === "lost"
                ? "Motif de perte"
                : statusDialog.status === "meeting_scheduled"
                  ? "Planifier le rendez-vous"
                  : "Ajouter une interaction"}
            </DialogTitle>
          </DialogHeader>
          <StatusExtraFields status={statusDialog.status} value={statusExtra} onChange={setStatusExtra} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setStatusDialog({ open: false, status: null })}>
              Annuler
            </Button>
            <Button
              data-testid="btn-confirm-status"
              onClick={confirmStatusChange}
            >
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assigner {p.pharmacy_name}</DialogTitle>
          </DialogHeader>
          <RadioGroup value={assignUserId} onValueChange={setAssignUserId} data-testid="radio-assign-user">
            <label className="flex items-center gap-2 py-1.5 cursor-pointer">
              <RadioGroupItem value="__none__" />
              <span className="text-sm text-slate-700">Non assigné</span>
            </label>
            {directory.map((u) => (
              <label key={u.id} className="flex items-center gap-2 py-1.5 cursor-pointer">
                <RadioGroupItem value={u.id} />
                <span className="text-sm text-slate-700">{u.name}</span>
              </label>
            ))}
          </RadioGroup>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAssignDialogOpen(false)} disabled={savingAssign}>Annuler</Button>
            <Button
              onClick={saveAssign}
              disabled={savingAssign}
              data-testid="btn-save-assign"
            >
              {savingAssign ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
              Assigner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteWithReasonDialog
        target={deletingInteraction} testId="delete-interaction" onClose={() => setDeletingInteraction(null)}
        title="Supprimer cet échange ?"
        description="Il va dans la corbeille et se restaure pendant 30 jours. Il disparaît de l'historique de la fiche et du suivi du dernier contact."
        onConfirm={async (reason) => {
          const target = deletingInteraction;
          setDeletingInteraction(null);
          try {
            await api.delete(`/prospects/${p.id}/interactions/${target.id}`, { data: { reason } });
            toast.success("Échange mis à la corbeille");
            load();
          } catch (e) {
            toast.error(errorMessage(e, "Impossible de supprimer cet échange"));
          }
        }}
      />
      <Dialog open={interactionDialogOpen} onOpenChange={setInteractionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter une interaction</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={interactionKind} onValueChange={setInteractionKind}>
                <SelectTrigger className="mt-1.5" data-testid="select-interaction-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INTERACTION_TYPES.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Compte-rendu</Label>
              <Textarea
                data-testid="input-interaction-content"
                rows={4}
                placeholder="Ce qui s'est passé…"
                value={interactionContent}
                onChange={(e) => setInteractionContent(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setInteractionDialogOpen(false)} disabled={savingInteraction}>Annuler</Button>
            <Button
              onClick={saveInteraction}
              disabled={savingInteraction}
              data-testid="btn-save-interaction"
            >
              {savingInteraction ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TaskFormSheet
        open={taskFormOpen} onOpenChange={setTaskFormOpen} task={editingTask}
        canScopeAll={canScopeAllTasks} canEdit={canEditTasks} directory={directory} onSaved={onTaskSaved} onComplete={(task, payload) => setCompleting({ task, payload })}
        lockedProspect={{ id: p.id, pharmacy_name: p.pharmacy_name }}
      />

      <TaskDetailSheet
        task={viewingTask} onOpenChange={(open) => !open && setViewingTask(null)}
        canEdit={canEditTasks} canDelete={canDeleteTasks} directory={directory} directoryNames={directoryNames}
        resolveAssignee={resolveAssignee} currentUser={user}
        navigate={navigate} onEdit={openEditTask} onDelete={setDeletingTask} onMarkDone={markTaskDone}
        onPostpone={postponeTask} onAddAssignee={addTaskAssignee} onCycleStatus={cycleTaskStatus}
      />

      <CompleteTaskDialog task={completing?.task} user={user} onClose={() => setCompleting(null)} onConfirm={(_, comment, performer) => confirmComplete(comment, performer)}
        performers={taskPerformerOptions(completing?.task, pilotState.status === "ready" ? pilotState.data : null, id)} />
      <DeleteTaskDialog task={deletingTask} onOpenChange={(open) => !open && setDeletingTask(null)} onConfirm={confirmDeleteTask} />
    </div>
  );
}

function CircleDotIcon() {
  return (
    <span className="relative inline-flex w-3 h-3 items-center justify-center">
      <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-70" />
      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
    </span>
  );
}
